/* eslint-disable max-len */
import { handle } from 'redux-pack';
import { isEqual } from 'underscore';
import {
	LOAD_GENERATE_CONTENT_TEMPLATES,
	LOAD_GENERATED_CONTENT,
	LOAD_GENERATED_CONTENT_BY_ID,
	REMOVE_GENERATED_CONTENT_BY_ID,
	CONTENT_ENTITY_UPDATED,
	CONTENT_ENTITY_UPDATED_DRAFT,
	LOAD_DRAFT_CONTENT,
	CLEAR_DRAFT_CONTENT,
	SET_SELECTED_MEDIA_TEMPLATE,
	SET_STYLE_PACK_DRAWER,
	UPSERT_CONTENT,
	ADD_INITIALIZING_JOB,
	REMOVE_INITIALIZING_JOB,
} from '../actions/generate-content';
import type { MediaItem } from '../types/media';
import { GeneratedContent, GeneratedContentData } from '../types/content';
import { ContentTemplate } from '../../components/types/stylepacks';
import { decodeHtmlEntities } from '../../utils/utils';
import type { GeneratedContentState } from '../types/generate-content';

export type { GeneratedContentState } from '../types/generate-content';

type InitializingJobMap = Map<string, { data: GeneratedContentData[], version: number }>;

export const HangingUserKey = (fkTable: string, fkId?: number) => `${fkTable}-associated:${fkId}`

export const initialGeneratedContentState: GeneratedContentState = {
	contentTemplates: [],
	generatedContent: [],
	generatedContentEdits: [],
	loadingTemplates: false,
	loadingGeneratedContent: false,
	selectedMediaItem: null,
	selectedMediaTemplate: '',
	selectedStylePackDrawer: 'style-packs',
	initializingJobs: new Map(),
	upsertingContent: false,
};

interface GeneratedContentAction {
	type: string;
	payload: any;
	id?: number;
}

const reduceFunction = (acc: GeneratedContent[], content: GeneratedContent) => {
	// first value is always going to be the new item
	if (acc[0].id === content.id) {
		const existingContent = acc.shift();
		acc.push(existingContent ?? content);
	} else {
		acc.push(content);
	}
	return acc;
};

const keysToIgnore = ['compscale', 'speaker${i}-headshot', 'speaker${i}-name', 'speaker${i}-company', 'speaker${i}-position'];
const filterKey = (dataField: GeneratedContentData) => !keysToIgnore.includes(dataField.key);

const removeInitializingJob = (jobs: InitializingJobMap, content: GeneratedContent, isDraft: boolean) => {
	const newJobs = new Map(jobs);
	const key = `${content.fkTable}-${content.fkId}${isDraft ? '-draft' : ''}`;
	const existingJob = newJobs.get(key);
	if (existingJob && content.version === existingJob.version) {
		newJobs.delete(key);
	}
	return newJobs;
};

const actions: Map<any, Function> = new Map([
	[
		LOAD_GENERATE_CONTENT_TEMPLATES,
		(state: GeneratedContentState, action: GeneratedContentAction) => handle(state, action, {
			start: (s:GeneratedContentState) => ({
				...s,
				loadingTemplates: true,
			}),
			finish: (s:GeneratedContentState) => ({
				...s,
				loadingTemplates: false,
			}),
			failure: (s:GeneratedContentState) => ({
				...s,
				loadingTemplates: false,
			}),
			success: (s:GeneratedContentState) => {
				const contentTemplates = decodeHtmlEntities(action.payload?.templates) ?? [];
				return {
					...s,
					contentTemplates,
				};
			},
		}),
	],
	[
		LOAD_GENERATED_CONTENT,
		(state: GeneratedContentState, action: GeneratedContentAction) => handle(state, action, {
			start: (s:GeneratedContentState) => ({
				...s,
				loadingGeneratedContent: true,
			}),
			finish: (s:GeneratedContentState) => ({
				...s,
				loadingGeneratedContent: false,
			}),
			failure: (s:GeneratedContentState) => ({
				...s,
				loadingGeneratedContent: false,
			}),
			success: (s:GeneratedContentState) => {
				const generatedContent = decodeHtmlEntities(action.payload?.generatedContent) ?? [];
				return {
					...s,
					generatedContent,
				};
			},
		}),
	],
	[
		LOAD_GENERATED_CONTENT_BY_ID,
		(state: GeneratedContentState, action: GeneratedContentAction) => handle(state, action, {
			start: (s:GeneratedContentState) => s,
			finish: (s:GeneratedContentState) => s,
			failure: (s:GeneratedContentState) => s,
			success: (s:GeneratedContentState) => {
				const generatedContent = [...s.generatedContent];
				const index = generatedContent?.findIndex(
					(content: GeneratedContent) => content.id === (action?.payload?.generatedContent?.id ?? 0),
				) ?? -1;

				if (index > -1) {
					if (generatedContent[index].version <= action?.payload?.generatedContent?.version) {
						generatedContent[index] = action.payload?.generatedContent ?? {};
					} else {
						console.log(`current version ${generatedContent[index].version} is greater than ${action?.payload?.generatedContent?.version} and ignoring update`);
					}
				} else if (action.payload?.generatedContent?.id) {
					// put it at the top of the list
					generatedContent.unshift(action.payload?.generatedContent ?? {});
				}

				return {
					...s,
					generatedContent,
				};
			},
		}),
	],
	[
		REMOVE_GENERATED_CONTENT_BY_ID,
		(state: GeneratedContentState, action: GeneratedContentAction) => handle(state, action, {
			start: (s:GeneratedContentState) => s,
			finish: (s:GeneratedContentState) => s,
			failure: (s:GeneratedContentState) => s,
			success: (s:GeneratedContentState) => {
				const generatedContent = [...s.generatedContent];
				const index = generatedContent.findIndex(
					(content: GeneratedContent) => content.id === (action?.payload ?? 0),
				);
				if (index > -1) {
					generatedContent.splice(index, 1);
				}

				return {
					...s,
					generatedContent,
				};
			},
		}),
	],
	[
		CONTENT_ENTITY_UPDATED,
		(state: GeneratedContentState, action: GeneratedContentAction) => {
			const sanitizedPayload = decodeHtmlEntities(action.payload.entityValue);
			return ({
				...state,
				generatedContent: [...state.generatedContent.reduce(
					reduceFunction,
					[sanitizedPayload],
				)],
				initializingJobs: removeInitializingJob(state.initializingJobs, sanitizedPayload, false),
			});
		},
	],
	[
		CONTENT_ENTITY_UPDATED_DRAFT,
		(state: GeneratedContentState, action: GeneratedContentAction) => {
			const sanitizedPayload = decodeHtmlEntities(action.payload.entityValue);

			return ({
				...state,
				generatedContentEdits: [...state.generatedContentEdits.reduce(
					reduceFunction,
					[sanitizedPayload],
				)],
				initializingJobs: removeInitializingJob(state.initializingJobs, sanitizedPayload, true),
			});
		},
	],
	[
		CLEAR_DRAFT_CONTENT,
		(state: GeneratedContentState) => {
			const newJobs = new Map(state.initializingJobs);
			newJobs.forEach((_, key) => { if (key.endsWith('-draft')) newJobs.delete(key); });
			return {
				...state,
				generatedContentEdits: [],
				initializingJobs: newJobs,
			};
		},
	],
	[
		LOAD_DRAFT_CONTENT,
		(state: GeneratedContentState, action: GeneratedContentAction) => handle(state, action, {
			start: (s) => s,
			finish: (s) => s,
			failure: (s) => s,
			success: (s) => ({ ...s, generatedContentEdits: action.payload }),
		}),
	],
	[
		UPSERT_CONTENT,
		(state: GeneratedContentState, action: GeneratedContentAction) => handle(state, action, {
			start: (s) => ({ ...s, upsertingContent: true }),
			finish: (s) => ({ ...s, upsertingContent: false }),
			failure: (s) => ({ ...s, upsertingContent: false }),
			success: (s) => ({ ...s, upsertingContent: false }),
		}),
	],
	[
		SET_SELECTED_MEDIA_TEMPLATE,
		(state: GeneratedContentState, action: GeneratedContentAction) => ({
			...state,
			selectedMediaTemplate: action.payload.template,
			selectedMediaItem: action.payload.mediaItem,
		}),
	],
	[
		SET_STYLE_PACK_DRAWER,
		(state: GeneratedContentState, action: GeneratedContentAction) => ({
			...state,
			selectedStylePackDrawer: action.payload,
		}),
	],
	[
		ADD_INITIALIZING_JOB,
		(state: GeneratedContentState, action: GeneratedContentAction) => {
			const newInitializingJobs = new Map(state.initializingJobs);
			newInitializingJobs.set(action.payload.key, {
				data: decodeHtmlEntities(action.payload.data),
				version: action.payload.version,
			});

			return {
				...state,
				initializingJobs: newInitializingJobs,
			};
		},
	],
	[
		REMOVE_INITIALIZING_JOB,
		(state: GeneratedContentState, action: GeneratedContentAction) => {
			const newInitializingJobs = new Map(state.initializingJobs);
			newInitializingJobs.delete(action.payload);

			return {
				...state,
				initializingJobs: newInitializingJobs,
			};
		},
	],
]);

export const GeneratedContentReducer = (
	state: GeneratedContentState = initialGeneratedContentState,
	action: GeneratedContentAction = { type: '', payload: {} },
) => actions.get(action.type)?.(state, action) ?? state;
